// GlobalSearch — Topbar quick-search across Clients/Sites/Buildings/Projects/
// Actions/Incidents/Contacts/Documents. Debounced, keyboard-navigable, results
// are already RBAC + tenant scoped server-side (see routes/search_routes.py).
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
    Search, Loader2, X, Briefcase, MapPin, Building, FolderOpen,
    CheckSquare, AlertTriangle, Users, FileText,
} from "lucide-react";
import { api } from "@/lib/api";
import { TID } from "@/lib/testIds";

const CATEGORY_ICON = {
    clients: Briefcase, sites: MapPin, buildings: Building, projects: FolderOpen,
    actions: CheckSquare, incidents: AlertTriangle, contacts: Users, documents: FileText,
};

export default function GlobalSearch() {
    const [query, setQuery] = useState("");
    const [open, setOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const [groups, setGroups] = useState([]);
    const [activeIdx, setActiveIdx] = useState(-1);
    const navigate = useNavigate();
    const boxRef = useRef(null);
    const debounceRef = useRef(null);

    const flatResults = groups.flatMap((g) => g.results.map((r) => ({ ...r, category: g.category })));

    useEffect(() => {
        if (debounceRef.current) clearTimeout(debounceRef.current);
        const q = query.trim();
        if (q.length < 2) { setGroups([]); setLoading(false); return undefined; }
        setLoading(true);
        debounceRef.current = setTimeout(async () => {
            try {
                const { data } = await api.get("/search", { params: { q } });
                setGroups(data.groups || []);
            } catch {
                setGroups([]);
            } finally { setLoading(false); }
        }, 300);
        return () => clearTimeout(debounceRef.current);
    }, [query]);

    useEffect(() => {
        function onClick(e) { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); }
        document.addEventListener("mousedown", onClick);
        return () => document.removeEventListener("mousedown", onClick);
    }, []);

    function goTo(result) {
        setOpen(false); setQuery(""); setGroups([]); setActiveIdx(-1);
        navigate(result.link);
    }

    function onKeyDown(e) {
        if (!open || !flatResults.length) return;
        if (e.key === "ArrowDown") { e.preventDefault(); setActiveIdx((i) => Math.min(i + 1, flatResults.length - 1)); }
        else if (e.key === "ArrowUp") { e.preventDefault(); setActiveIdx((i) => Math.max(i - 1, 0)); }
        else if (e.key === "Enter") { e.preventDefault(); if (activeIdx >= 0) goTo(flatResults[activeIdx]); }
        else if (e.key === "Escape") { setOpen(false); }
    }

    const showDropdown = open && query.trim().length >= 2;

    return (
        <div ref={boxRef} className="relative hidden md:block w-[280px]">
            <div data-testid={TID.topbarSearch} className="flex items-center gap-2 bg-white border rounded-[9px] px-3 py-2"
                 style={{ borderColor: "#E5E0DA" }}>
                {loading
                    ? <Loader2 className="w-[15px] h-[15px] animate-spin flex-shrink-0" style={{ color: "#6F6A64" }} />
                    : <Search className="w-[15px] h-[15px] flex-shrink-0" style={{ color: "#6F6A64" }} />}
                <input
                    type="text"
                    value={query}
                    onChange={(e) => { setQuery(e.target.value); setOpen(true); setActiveIdx(-1); }}
                    onFocus={() => setOpen(true)}
                    onKeyDown={onKeyDown}
                    placeholder="Search clients, buildings, actions..."
                    className="flex-1 outline-none border-none bg-transparent text-[12.5px] placeholder:text-[#9A968F]"
                    data-testid="global-search-input"
                />
                {query && (
                    <button data-testid="global-search-clear" onClick={() => { setQuery(""); setGroups([]); }}
                            className="text-[#9A968F] hover:text-[#6F6A64] flex-shrink-0">
                        <X className="w-3.5 h-3.5" />
                    </button>
                )}
            </div>

            {showDropdown && (
                <div data-testid="global-search-results"
                     className="absolute left-0 right-0 top-[110%] bg-white border rounded-xl shadow-brand max-h-[440px] overflow-y-auto z-40 animate-fade-in"
                     style={{ borderColor: "#E5E0DA", minWidth: "320px" }}>
                    {loading && groups.length === 0 && (
                        <div className="px-4 py-6 text-center text-[12px]" style={{ color: "#9A968F" }}>Searching…</div>
                    )}
                    {!loading && groups.length === 0 && (
                        <div data-testid="global-search-empty" className="px-4 py-6 text-center text-[12px]" style={{ color: "#9A968F" }}>
                            No matches for &ldquo;{query.trim()}&rdquo;
                        </div>
                    )}
                    {groups.map((g) => (
                        <div key={g.category} className="border-b last:border-b-0" style={{ borderColor: "#F0EDE8" }}>
                            <div className="px-3.5 pt-2.5 pb-1 text-[10px] font-bold uppercase tracking-wider" style={{ color: "#9A968F" }}>
                                {g.label}
                            </div>
                            {g.results.map((r) => {
                                const flatIdx = flatResults.findIndex((f) => f.category === g.category && f.id === r.id);
                                const Icon = CATEGORY_ICON[g.category] || Search;
                                return (
                                    <button
                                        key={r.id}
                                        data-testid={`global-search-result-${g.category}-${r.id}`}
                                        onClick={() => goTo(r)}
                                        onMouseEnter={() => setActiveIdx(flatIdx)}
                                        className="w-full text-left flex items-center gap-2.5 px-3.5 py-2 transition-colors"
                                        style={{ background: activeIdx === flatIdx ? "#F6F4F0" : "transparent" }}
                                    >
                                        <Icon className="w-3.5 h-3.5 flex-shrink-0" style={{ color: "var(--brand-accent,#C75A1B)" }} />
                                        <div className="flex-1 min-w-0">
                                            <div className="text-[12.5px] font-semibold text-[#1F1F1F] truncate">{r.title}</div>
                                            {r.subtitle && <div className="text-[11px] truncate" style={{ color: "#6F6A64" }}>{r.subtitle}</div>}
                                        </div>
                                    </button>
                                );
                            })}
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}
